import React, { createContext, useCallback, useContext, useState, useEffect, useRef } from "react";
import { authAPI, profileAPI } from "../services/api";
import { tokenStore, isAuthRejection, setReloginCredentials } from "../services/platformClient";
import outbox from "../services/attendanceOutbox";
import { clearCache } from "../utils/cache";
import { flattenUser, isAdminRole } from "../utils/userAdapter";
import { saveOfflineCredential, updateOfflineUser, verifyOfflineCredential } from "../utils/offlineAuth";

/**
 * AUTH PLATFORM (standalone, bukan Odoo).
 * ----------------------------------------------------------------------------
 * - Login sekali ke backend platform (username+password). Untuk karyawan,
 *   "NIK" dikirim sebagai username & "PIN" sebagai password.
 * - Setelah login, ambil /profile untuk melengkapi data karyawan, lalu simpan
 *   objek user "flat" (lihat userAdapter) supaya UI lama tetap jalan.
 * - `role` menentukan apakah user boleh membuka area admin (isAdmin).
 *
 * MODE OFFLINE (`offline` = true): server/jaringan belum siap tapi user tetap
 * masuk memakai data tersimpan — baik saat app dibuka ulang (sesi lama) maupun
 * login ulang (NIK+PIN dicocokkan ke verifier lokal, lihat utils/offlineAuth).
 * Absen masuk antrean HP. Begitu server kembali: sesi dipulihkan (refresh
 * cookie, atau login ulang diam-diam pakai PIN di memory) lalu antrean dikirim.
 * Logout paksa HANYA bila server benar-benar menolak sesi (401/403).
 */
const AuthContext = createContext(null);

const RECONNECT_INTERVAL_MS = 20000;
// Logout yang belum sampai ke server (dilakukan saat offline). Selama ada,
// refresh cookie lama TIDAK boleh dipakai memulihkan sesi.
const PENDING_LOGOUT_KEY = "hris_pending_logout";

/** Gagal karena jaringan/timeout/server bermasalah (bukan ditolak)? */
const isUnreachable = (err) => !err?.response || err.response.status >= 500;

function setPendingLogout(on) {
  try {
    if (on) localStorage.setItem(PENDING_LOGOUT_KEY, "1");
    else localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch { /* storage terblokir */ }
}

function hasPendingLogout() {
  try { return localStorage.getItem(PENDING_LOGOUT_KEY) === "1"; } catch { return false; }
}

/** Kirim logout tertunda. true = beres (atau memang tak ada), false = server belum terjangkau. */
async function flushPendingLogout() {
  if (!hasPendingLogout()) return true;
  try {
    await authAPI.logout();
  } catch (err) {
    if (isUnreachable(err)) return false;
  }
  setPendingLogout(false);
  return true;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const reconnecting = useRef(false);

  const clearAuthState = useCallback(() => {
    clearCache();
    tokenStore.clear();
    setUser(null);
    setOffline(false);
    setLoading(false);
    setError(null);
  }, []);

  // Bangun user flat dari /auth/me (punya role_name) + /profile (punya employee).
  const buildFullUser = useCallback(async (loginUser) => {
    let me = loginUser || null;
    try {
      if (!me) me = await authAPI.me();
    } catch {
      /* biarkan; pakai loginUser bila ada */
    }
    let profile = null;
    try {
      profile = await profileAPI.get({ force: true }); // sudah flat
    } catch {
      /* profil bisa gagal kalau user belum di-mapping ke employee */
    }
    // profile sudah flat; gabungkan role_name dari login/me kalau ada.
    const flat = profile
      ? { ...profile, role: me?.role || profile.role, role_name: me?.role_name || profile.role_name, status: me?.status || profile.status }
      : flattenUser({ user: me });
    return flat;
  }, []);

  const applyOnlineUser = useCallback((full) => {
    tokenStore.setUser(full);
    updateOfflineUser(full);
    setUser(full);
    setOffline(false);
  }, []);

  const expireToLogin = useCallback(() => {
    clearAuthState();
    if (window.location.pathname !== "/login") window.location.href = "/login";
  }, [clearAuthState]);

  // Saat mount: kalau ada token, verifikasi & lengkapi data user.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!(await flushPendingLogout())) throw new Error("offline");
        if (!tokenStore.getAccess()) await authAPI.refresh();
        const me = await authAPI.me();
        const full = await buildFullUser(me);
        if (!cancelled) applyOnlineUser(full);
      } catch (err) {
        if (cancelled) return;
        // Server belum siap tapi sesi pernah ada → tetap masuk (mode offline).
        const cached = tokenStore.getUser();
        const unreachable = err?.message === "offline" || !isAuthRejection(err);
        if (cached && unreachable) {
          setUser(cached);
          setOffline(true);
        } else {
          clearAuthState();
        }
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    window.addEventListener("hris:session-expired", expireToLogin);
    return () => {
      cancelled = true;
      window.removeEventListener("hris:session-expired", expireToLogin);
    };
  }, [buildFullUser, clearAuthState, applyOnlineUser, expireToLogin]);

  // Mode offline: coba pulihkan sesi server berkala & saat koneksi kembali,
  // lalu kirim antrean absen.
  const tryReconnect = useCallback(async () => {
    if (reconnecting.current) return;
    reconnecting.current = true;
    try {
      if (!(await flushPendingLogout())) return;
      await authAPI.refresh(); // refresh cookie, atau login ulang diam-diam (PIN di memory)
      const full = await buildFullUser();
      applyOnlineUser(full);
      outbox.flush({ force: true });
    } catch (err) {
      if (isAuthRejection(err)) expireToLogin(); // sesi tak bisa dipulihkan → login online
    } finally {
      reconnecting.current = false;
    }
  }, [buildFullUser, applyOnlineUser, expireToLogin]);

  useEffect(() => {
    if (!offline) return undefined;
    const kick = () => tryReconnect();
    const timer = setInterval(kick, RECONNECT_INTERVAL_MS);
    window.addEventListener("online", kick);
    window.addEventListener("hris:app-refresh", kick);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", kick);
      window.removeEventListener("hris:app-refresh", kick);
    };
  }, [offline, tryReconnect]);

  /** Login pakai verifier lokal saat server tak terjangkau. */
  const loginOffline = useCallback(async (nik, pin) => {
    const res = await verifyOfflineCredential(nik, pin);
    if (res.ok) {
      tokenStore.setUser(res.user);
      setReloginCredentials({ nik, pin });
      setUser(res.user);
      setOffline(true);
      return true;
    }
    const messages = {
      wrong_pin: `PIN salah. Sisa ${res.remaining} percobaan saat offline.`,
      locked: "Terlalu banyak PIN salah. Login offline dikunci — tunggu server kembali online.",
      expired: "Login offline kedaluwarsa. Tunggu server kembali online untuk login.",
      not_found: "Server HR belum bisa dihubungi, dan akun ini belum pernah login online di HP ini.",
      unsupported: "Server HR belum bisa dihubungi. Login offline tidak didukung di browser ini.",
    };
    setError(messages[res.reason] || "Server HR belum bisa dihubungi. Coba lagi.");
    return false;
  }, []);

  const login = useCallback(
    async (nik, pin) => {
      setLoading(true);
      setError(null);
      const cleanNik = String(nik).trim();
      try {
        // 1) Autentikasi → dapat token + refresh + user (dgn role_name).
        const data = await authAPI.login(cleanNik, pin);
        tokenStore.setTokens(data);
        // 2) Lengkapi profil karyawan.
        clearCache();
        const full = await buildFullUser(data.user);
        applyOnlineUser(full);
        setPendingLogout(false); // sesi baru menggantikan cookie lama
        // 3) Simpan verifier untuk login offline berikutnya (PIN tidak disimpan).
        saveOfflineCredential(cleanNik, pin, full);
        outbox.flush({ force: true });
        return true;
      } catch (err) {
        tokenStore.clear();
        // Server/jaringan belum siap → coba login offline.
        if (isUnreachable(err)) return loginOffline(cleanNik, pin);

        const status = err.response?.status;
        const backendMsg = err.response?.data?.error?.message;
        if (status === 401) {
          setError(backendMsg || "NIK atau PIN salah. Cek lagi ya.");
        } else if (status === 403) {
          setError(backendMsg || "Akun tidak aktif. Hubungi HR.");
        } else {
          setError(backendMsg || "Login belum berhasil. Coba lagi.");
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [buildFullUser, applyOnlineUser, loginOffline]
  );

  const logout = useCallback(() => {
    setPendingLogout(true);
    authAPI.logout().then(() => setPendingLogout(false)).catch((err) => {
      if (!isUnreachable(err)) setPendingLogout(false);
    });
    clearAuthState();
  }, [clearAuthState]);

  const isAdmin = !!user && isAdminRole(user.role);

  return (
    <AuthContext.Provider
      value={{ user, loading, initializing, error, login, logout, setError, isAdmin, offline }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/** Cek apakah user punya salah satu role. */
export function hasRole(user, ...roles) {
  return !!user && roles.includes(user.role);
}
